import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Check, LogOut, Zap } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PLANS = [
  {
    lookup_key: "basic_monthly", name: "Basic", price: "4.99", accent: "#FF3B30",
    tagline: "Track everything.",
    features: ["Workout logging", "Body weight tracking", "Gym reminders", "Progress charts & stats"],
  },
  {
    lookup_key: "pro_monthly", name: "Pro", price: "14.99", accent: "#007AFF", featured: true,
    tagline: "Add your AI coach.",
    features: ["Everything in Basic", "AI Physical Coach (Claude)", "Unlimited coaching chats", "Personalized programming"],
  },
];

export default function Plans() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  const subscribe = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout");
      setBusy(null);
    }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative z-10">
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flame className="text-[#FF3B30]" size={24} strokeWidth={2.5} />
          <span className="font-display font-black text-2xl uppercase tracking-tighter">Reprise<span className="text-[#FF3B30]">Fit</span></span>
        </div>
        <button onClick={handleLogout} data-testid="plans-logout" className="text-white/50 hover:text-white flex items-center gap-2 text-sm uppercase tracking-wide"><LogOut size={16} /> Log out</button>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 bg-[#34C759]/15 text-[#34C759] px-3 py-1.5 text-xs font-bold uppercase tracking-widest mb-6">
            <Zap size={14} /> 7 days free · cancel anytime
          </div>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-6xl uppercase tracking-tighter leading-none text-center mb-3">
          Choose your <span className="text-[#FF3B30]">plan</span>
        </h1>
        <p className="text-white/50 text-center mb-14 max-w-lg mx-auto">Start with a 7-day free trial. You won't be charged until the trial ends, and you can cancel anytime.</p>

        <div className="grid md:grid-cols-2 gap-5">
          {PLANS.map((p) => (
            <div key={p.lookup_key} data-testid={`plan-${p.name.toLowerCase()}`}
              className={`relative p-8 bg-[#121212] border transition-transform duration-150 hover:-translate-y-1 ${p.featured ? "border-[#007AFF]/50" : "border-white/10"}`}>
              {p.featured && <div className="absolute -top-3 left-8 bg-[#007AFF] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1">Most Popular</div>}
              <h2 className="font-display font-black text-3xl uppercase tracking-tight" style={{ color: p.accent }}>{p.name}</h2>
              <p className="text-white/50 text-sm mb-6">{p.tagline}</p>
              <div className="flex items-end gap-1 mb-6">
                <span className="font-display font-black text-5xl tabular-nums">${p.price}</span>
                <span className="text-white/40 mb-1.5">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {p.features.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-white/80 text-sm"><Check size={16} style={{ color: p.accent }} /> {f}</li>
                ))}
              </ul>
              <button
                data-testid={`subscribe-${p.lookup_key}`}
                onClick={() => subscribe(p.lookup_key)}
                disabled={busy}
                className="w-full font-bold uppercase tracking-wide px-6 py-3.5 transition-transform duration-150 hover:-translate-y-0.5 disabled:opacity-50 text-white"
                style={{ background: p.accent }}
              >
                {busy === p.lookup_key ? "Redirecting..." : "Start Free Trial"}
              </button>
            </div>
          ))}
        </div>
        <p className="text-white/30 text-xs text-center mt-8">Test mode — use card 4242 4242 4242 4242, any future expiry, any CVC.</p>
      </div>
    </div>
  );
}
